import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import MDBox from '../md/MDBox';
import { Spinner } from 'src/components/ui/spinner';
import { Button } from 'src/components/ui/button';
import {
  XIcon,
  ArrowSquareOutIcon,
  ArrowsOutIcon,
  ArrowsInIcon,
} from '@phosphor-icons/react';
import ApiError from 'src/api/ApiError';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import PrimaryButton from 'src/components/common/PrimaryButton';
import CancelButton from 'src/components/common/CancelButton';
import { NavigateOptions, useNavigate } from '@tanstack/react-router';
import { useId, useRef, useState } from 'react';
import MDButton from '../md/MDButton';
import { useGT } from 'gt-react';

interface ButtonProps {
  text: string;
  disabled?: boolean;
  color?: 'primary' | 'error' | 'info' | 'warning';
  variant?: 'text' | 'outlined' | 'contained';
  onClick?: () => void;
}

interface Props {
  open: boolean;
  title?: React.ReactNode;
  subheader?: React.ReactNode;
  children: React.ReactNode;
  onConfirmButton?: ButtonProps;
  onSecondOptionButton?: ButtonProps;
  additionalButtons?: ButtonProps[];
  onCloseButton: ButtonProps;
  isLoading?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullScreen?: boolean;
  fullScreenable?: boolean;
  closeable?: boolean;
  hideActions?: boolean;
  disableBackdropClickClose?: boolean;
  disableEscapeKeyClose?: boolean;
  disableContentOverflow?: boolean;
  error?: ApiError | null;
  openToPath?: NavigateOptions;
  hideCloseButton?: boolean;
  optionsMenu?: React.ReactElement | null;
  dialogSx?: React.CSSProperties;
  /**
   * Set when this modal opens on top of another modal. The base DialogContent
   * hardcodes `z-[1400]`, so a nested modal would tie with its parent and can
   * render behind it. This bumps the nested content above the parent.
   *
   * 1450 and not something higher: every popper surface (Popover, Select,
   * DropdownMenu, Tooltip) is portaled to `body` at `z-[1500]`, so a nested
   * modal above that line hides its own dropdowns behind itself (SUPP-198).
   * The nested overlay stays at 1400 and still covers the parent's content —
   * it's portaled later, and equal z-index resolves by DOM order.
   */
  nested?: boolean;
}
const Modal = ({
  open,
  title,
  subheader,
  children,
  onConfirmButton,
  onSecondOptionButton,
  additionalButtons,
  onCloseButton,
  isLoading,
  maxWidth,
  fullScreen,
  fullScreenable,
  closeable,
  hideActions,
  disableBackdropClickClose,
  disableEscapeKeyClose,
  disableContentOverflow,
  error,
  openToPath,
  hideCloseButton,
  optionsMenu,
  dialogSx,
  nested,
}: Props) => {
  const navigate = useNavigate();
  const gt = useGT();
  const titleId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [isFullScreen, setIsFullScreen] = useState(fullScreen || false);

  // NOTE: the base shadcn DialogContent ships `sm:max-w-lg`. A plain
  // `max-w-*` (base variant) does NOT override a `sm:`-prefixed one via
  // tailwind-merge, so an unprefixed width would leave every modal capped at
  // 512px on ≥sm screens. Emit the `sm:` variant so it wins on desktop while
  // the base `max-w-[calc(100%-2rem)]` keeps the mobile gutter.
  const maxWidthClass = {
    xs: 'sm:max-w-xs',
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-2xl',
    lg: 'sm:max-w-4xl',
    xl: 'sm:max-w-6xl',
  }[maxWidth ?? 'sm'];

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          if (disableBackdropClickClose && disableEscapeKeyClose) {
            return;
          }
          if (onCloseButton.onClick) {
            onCloseButton.onClick();
          }
        }
      }}
      // Modal's own props, forwarded to the dialog. Radix took these as
      // `onEscapeKeyDown` / `onInteractOutside` + `preventDefault` on the
      // *content*; Base UI reports the reason on the root's change event, so
      // `@erp/ui`'s Dialog now names them — which happens to be the vocabulary
      // Modal was already using.
      disableEscapeKeyClose={disableEscapeKeyClose}
      disableBackdropClickClose={disableBackdropClickClose}
    >
      <DialogContent
        // Modal renders its own close control in the header (see `closeable`
        // below), so suppress the built-in shadcn one to avoid a duplicate X.
        showCloseButton={false}
        initialFocus={closeable && title ? closeButtonRef : undefined}
        className={`${
          isFullScreen
            ? // True fullscreen: fill the viewport edge-to-edge. The base
              // DialogContent is a centered card (grid, rounded-lg, border,
              // shadow, translate-centering, sm:max-w-lg) — left intact it
              // leaves rounded corner notches + a border ring around the
              // screen and doesn't fill height. Override all of that.
              'flex flex-col w-screen h-screen max-w-none sm:max-w-none top-0 left-0 translate-x-0 translate-y-0 rounded-none border-0 shadow-none'
            : // The base DialogContent is vertically centered with no height
              // cap or overflow handling, so content taller than the viewport
              // spills off the top of the screen and can't be scrolled to.
              // Bound the dialog to the viewport and let the content area
              // (below) become the scroll region.
              `${maxWidthClass} max-h-[calc(100dvh-2rem)] flex flex-col`
        }${nested ? ' z-[1450]' : ''}`}
        style={dialogSx}
      >
        {title ? (
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle id={titleId}>{title}</DialogTitle>
            <MDBox display="flex" alignItems="center">
              {openToPath && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(openToPath)}
                  aria-label={gt('Open in current tab', {
                    context: 'modal toolbar, open linked route in this tab',
                  })}
                >
                  <ArrowSquareOutIcon size={20} />
                </Button>
              )}
              {optionsMenu}
              {fullScreenable && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsFullScreen(!isFullScreen)}
                  aria-label={gt('Toggle fullscreen', {
                    context: 'modal toolbar, expand or exit fullscreen',
                  })}
                >
                  {isFullScreen ? (
                    <ArrowsInIcon size={20} />
                  ) : (
                    <ArrowsOutIcon size={20} />
                  )}
                </Button>
              )}
              {closeable ? (
                <Button
                  ref={closeButtonRef}
                  variant="ghost"
                  size="icon"
                  onClick={onCloseButton.onClick}
                  aria-label={gt('Close', {
                    context: 'modal toolbar, dismiss dialog',
                  })}
                >
                  <XIcon size={20} />
                </Button>
              ) : (
                <MDBox />
              )}
            </MDBox>
          </DialogHeader>
        ) : (
          // Radix requires a DialogTitle on every DialogContent for screen-reader
          // accessibility; render a visually-hidden one when no title is provided.
          <DialogTitle className="sr-only">
            {gt('Dialog', {
              context: 'accessible label for an untitled modal',
            })}
          </DialogTitle>
        )}

        {subheader}

        {isFullScreen ? (
          <div
            className="flex-1 min-h-0 w-full p-2"
            style={{ overflowY: disableContentOverflow ? 'hidden' : 'auto' }}
          >
            {children}
          </div>
        ) : (
          <div
            className={
              disableContentOverflow
                ? 'overflow-hidden h-[80vh] min-h-[600px] flex flex-col p-2'
                : 'flex-1 min-h-0 overflow-y-auto'
            }
          >
            {children}
          </div>
        )}

        {!hideActions ? (
          <DialogFooter>
            {error ? (
              <MDBox>
                <ErrorMessage error={error} />
              </MDBox>
            ) : null}

            {!hideCloseButton && (
              <CancelButton
                onClick={onCloseButton?.onClick}
                disabled={onCloseButton?.disabled}
              >
                {onCloseButton?.text}
              </CancelButton>
            )}

            {additionalButtons?.map((button, index) => (
              <MDButton
                key={index}
                onClick={button.onClick}
                color={button.color}
                variant={button.variant}
                disabled={button.disabled}
              >
                {button.text}
              </MDButton>
            ))}

            {onSecondOptionButton && (
              <CancelButton
                onClick={onSecondOptionButton.onClick}
                disabled={onSecondOptionButton.disabled}
              >
                {onSecondOptionButton.text}
              </CancelButton>
            )}
            {onConfirmButton && (
              <PrimaryButton
                onClick={onConfirmButton.onClick}
                disabled={onConfirmButton.disabled}
              >
                {onConfirmButton.text}
              </PrimaryButton>
            )}
          </DialogFooter>
        ) : null}

        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70">
            <Spinner />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Modal;
